import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { WizardPublishResult } from 'fym-shared/src/smartlinks/wizardPublish';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { ToastType } from '../../../redux/slices/toasts/state';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../music24/hooks/useUpgradePrompt';

const messages = defineMessages({
  publishedTitle: { id: 'app.sml.create.publishNow.published.title', defaultMessage: 'Your page is live' },
  publishedBody: {
    id: 'app.sml.create.publishNow.published.body',
    defaultMessage: 'Fans can open it now. Share the link anywhere.',
  },
  slugTaken: {
    id: 'app.sml.create.publishNow.slugTaken',
    defaultMessage: 'Someone else has this address. Press Publish to pick a new one.',
  },
  failed: {
    id: 'app.sml.create.publishNow.failed',
    defaultMessage: 'We could not publish the page. Your changes are safe.',
  },
  draftTitle: { id: 'app.sml.create.publishNow.draft.title', defaultMessage: 'Saved as a draft' },
});

/**
 * Tells the artist what "publish at once" did (owner decision 6, 2026-10-20). Published: a toast with
 * the live address (the sheet closes and the editor opens, so the first-publish sheet of the editor
 * cannot open here). Refused: the page stays a draft, as before, plus one line with the reason. The
 * pages limit opens the plan prompt. A taken address names the Publish button of the editor, which
 * opens the slug-taken dialog. No `publishResult` (an older API, or no publish asked): nothing.
 */
export function useWizardPublishReport(): (
  publishResult: WizardPublishResult | undefined,
  shareUrl: string | null
) => void {
  const intl = useIntl();
  const { show } = useShowToast();
  const openUpgrade = useUpgradePrompt();
  return useCallback(
    (publishResult: WizardPublishResult | undefined, shareUrl: string | null) => {
      if (publishResult === undefined) {
        return;
      }
      if (publishResult.published) {
        show({
          type: ToastType.success,
          title: intl.formatMessage(messages.publishedTitle),
          description: intl.formatMessage(messages.publishedBody),
          linkUrl: shareUrl ?? undefined,
          autoHide: true,
        });
        return;
      }
      if (publishResult.refusedReason === 'pagesLimit') {
        openUpgrade(SMLUpgradeFeature.pages);
        return;
      }
      show({
        type: ToastType.warning,
        title: intl.formatMessage(messages.draftTitle),
        description: intl.formatMessage(
          publishResult.refusedReason === 'slugTaken' ? messages.slugTaken : messages.failed
        ),
        autoHide: true,
      });
    },
    [intl, openUpgrade, show]
  );
}
